import { useCallback, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { ConfigFileOverride, ConfigurationFile, EnvironmentSummary } from '@/api/configuration';
import {
    createConfigFileOverride,
    deleteConfigFileOverride,
    updateConfigFileOverride,
} from '@/api/configuration';
import type { OverrideFieldValues, OverrideValidationErrors } from '@/components/deploy/config-override-validation';
import { validateOverrideFields } from '@/components/deploy/config-override-validation';
import type { ResolvedConfigFields } from '@/components/deploy/resolve-config-fields';
import { resolveConfigFields } from '@/components/deploy/resolve-config-fields';
import { useConfigFileOverrides } from '@/hooks/use-config-overrides';
import { queryKeys } from '@/modules/query/query-keys';

// --- Props ---

interface EnvironmentOverridePanelProps {
  projectKey: string;
  configFile: ConfigurationFile;
  environments: EnvironmentSummary[];
}

// --- Helpers ---

function hasValidationErrors(errors: OverrideValidationErrors): boolean {
  return Object.values(errors).some((v) => !!v);
}

function ResolvedFieldIndicator({ source }: { source: 'override' | 'default' }) {
  if (source === 'override') {
    return (
      <span className="inline-flex items-center rounded-full bg-brand-100 dark:bg-brand-900/30 px-1.5 py-0.5 text-[10px] font-medium text-brand-700 dark:text-brand-300">
        Override
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] font-medium text-content-tertiary">
      Default
    </span>
  );
}

// --- Component ---

export function EnvironmentOverridePanel({
  projectKey,
  configFile,
  environments,
}: EnvironmentOverridePanelProps) {
  const [selectedEnvId, setSelectedEnvId] = useState(environments[0]?.id ?? '');
  const [sourceRoot, setSourceRoot] = useState('');
  const [targetLocation, setTargetLocation] = useState('');
  const [targetFilename, setTargetFilename] = useState('');
  const [errors, setErrors] = useState<OverrideValidationErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const queryClient = useQueryClient();
  const { data: overrides = [], isLoading } = useConfigFileOverrides(projectKey, configFile.id);

  // Find existing override for the selected environment
  const existingOverride = useMemo(
    () => overrides.find((o) => o.environmentId === selectedEnvId),
    [overrides, selectedEnvId]
  );

  // Resolve fields for display
  const resolved: ResolvedConfigFields = useMemo(
    () => resolveConfigFields(configFile, existingOverride),
    [configFile, existingOverride]
  );

  // When the selected environment changes, populate form with existing override data
  const handleEnvironmentChange = useCallback(
    (envId: string) => {
      setSelectedEnvId(envId);
      setErrors({});
      setApiError(null);
      const override = overrides.find((o) => o.environmentId === envId);
      if (override) {
        setSourceRoot(override.sourceRoot ?? '');
        setTargetLocation(override.targetLocation ?? '');
        setTargetFilename(override.targetFilename ?? '');
      } else {
        setSourceRoot('');
        setTargetLocation('');
        setTargetFilename('');
      }
    },
    [overrides]
  );

  // Sync form when overrides data changes (e.g. after save)
  const syncFormWithOverride = useCallback(
    (override: ConfigFileOverride | undefined) => {
      if (override) {
        setSourceRoot(override.sourceRoot ?? '');
        setTargetLocation(override.targetLocation ?? '');
        setTargetFilename(override.targetFilename ?? '');
      } else {
        setSourceRoot('');
        setTargetLocation('');
        setTargetFilename('');
      }
    },
    []
  );

  // Initialize form when component mounts or selectedEnvId first resolves
  useMemo(() => {
    syncFormWithOverride(existingOverride);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingOverride?.id]);

  const invalidateOverrides = useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeys.configuration.configFileOverrides(projectKey, configFile.id) }),
    [queryClient, projectKey, configFile.id]
  );

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    const values: OverrideFieldValues = { sourceRoot, targetLocation, targetFilename };
    const validationErrors = validateOverrideFields(values);
    setErrors(validationErrors);
    if (hasValidationErrors(validationErrors)) return;

    setSaving(true);
    try {
      if (existingOverride) {
        await updateConfigFileOverride(projectKey, configFile.id, existingOverride.id, {
          environmentId: existingOverride.environmentId,
          sourceRoot: sourceRoot || undefined,
          targetLocation: targetLocation || undefined,
          targetFilename: targetFilename || undefined,
        });
      } else {
        await createConfigFileOverride(projectKey, configFile.id, {
          environmentId: selectedEnvId,
          sourceRoot: sourceRoot || undefined,
          targetLocation: targetLocation || undefined,
          targetFilename: targetFilename || undefined,
        });
      }
      await invalidateOverrides();
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to save override');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingOverride) return;
    setApiError(null);
    setSaving(true);
    try {
      await deleteConfigFileOverride(projectKey, configFile.id, existingOverride.id);
      setSourceRoot('');
      setTargetLocation('');
      setTargetFilename('');
      await invalidateOverrides();
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to delete override');
    } finally {
      setSaving(false);
    }
  };

  if (environments.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-4 text-center">
        <p className="text-xs text-content-tertiary">No environments configured. Create environments in Admin first.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-surface-raised p-4 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-content-secondary">Environment Overrides</p>
        {existingOverride && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving}
            className="rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:opacity-50"
          >
            Delete Override
          </button>
        )}
      </div>

      {/* Environment selector */}
      <div>
        <label htmlFor="override-env-select" className="block text-xs font-medium text-content-secondary mb-1">
          Environment
        </label>
        <select
          id="override-env-select"
          value={selectedEnvId}
          onChange={(e) => handleEnvironmentChange(e.target.value)}
          className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
        >
          {environments.map((env) => (
            <option key={env.id} value={env.id}>
              {env.name}
            </option>
          ))}
        </select>
      </div>

      {/* Resolved fields display */}
      {!isLoading && (
        <div className="rounded-md border border-border bg-surface p-3 space-y-1.5">
          <p className="text-[10px] font-medium text-content-tertiary uppercase tracking-wide mb-1">Resolved Values</p>
          <div className="flex items-center gap-2 text-xs text-content-secondary">
            <span className="w-28 flex-shrink-0">Source Root:</span>
            <span className="truncate flex-1">{resolved.sourceRoot.value || '—'}</span>
            <ResolvedFieldIndicator source={resolved.sourceRoot.source} />
          </div>
          <div className="flex items-center gap-2 text-xs text-content-secondary">
            <span className="w-28 flex-shrink-0">Target Location:</span>
            <span className="truncate flex-1">{resolved.targetLocation.value || '—'}</span>
            <ResolvedFieldIndicator source={resolved.targetLocation.source} />
          </div>
          <div className="flex items-center gap-2 text-xs text-content-secondary">
            <span className="w-28 flex-shrink-0">Target Filename:</span>
            <span className="truncate flex-1">{resolved.targetFilename.value || '—'}</span>
            <ResolvedFieldIndicator source={resolved.targetFilename.source} />
          </div>
        </div>
      )}

      {/* Override form */}
      {isLoading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-8 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-8 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-8 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-3">
          <div>
            <label htmlFor="override-source-root" className="block text-xs font-medium text-content-secondary mb-1">
              Source Root
            </label>
            <input
              id="override-source-root"
              type="text"
              value={sourceRoot}
              onChange={(e) => setSourceRoot(e.target.value)}
              placeholder={configFile.defaultSourceRoot || 'Inherit from default'}
              className={`w-full rounded-md border px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600 ${
                errors.sourceRoot ? 'border-red-500' : 'border-border'
              } bg-surface`}
            />
            {errors.sourceRoot && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.sourceRoot}</p>}
          </div>

          <div>
            <label htmlFor="override-target-location" className="block text-xs font-medium text-content-secondary mb-1">
              Target Location
            </label>
            <input
              id="override-target-location"
              type="text"
              value={targetLocation}
              onChange={(e) => setTargetLocation(e.target.value)}
              placeholder={configFile.targetLocation || 'Inherit from default'}
              className={`w-full rounded-md border px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600 ${
                errors.targetLocation ? 'border-red-500' : 'border-border'
              } bg-surface`}
            />
            {errors.targetLocation && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.targetLocation}</p>}
          </div>

          <div>
            <label htmlFor="override-target-filename" className="block text-xs font-medium text-content-secondary mb-1">
              Target Filename
            </label>
            <input
              id="override-target-filename"
              type="text"
              value={targetFilename}
              onChange={(e) => setTargetFilename(e.target.value)}
              placeholder={configFile.targetFilename || 'Inherit from default'}
              className={`w-full rounded-md border px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600 ${
                errors.targetFilename ? 'border-red-500' : 'border-border'
              } bg-surface`}
            />
            {errors.targetFilename && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.targetFilename}</p>}
          </div>

          {apiError && (
            <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
              <p className="text-sm text-red-700 dark:text-red-300">{apiError}</p>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
            >
              {saving ? 'Saving...' : existingOverride ? 'Update Override' : 'Create Override'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
